/**
 * The one place the Unsplash credit is spelled.
 *
 * Their API Guidelines ask for a specific form — "Photo by Annie Spratt on
 * Unsplash" — with BOTH the photographer and the word Unsplash linked, and both
 * links carrying UTM parameters. Production rate limits are granted against
 * that checklist, and the reviewer is looking at screenshots, so an
 * almost-right credit on one surface is the whole application.
 *
 * It lives here rather than in each component because it already drifted: the
 * hero and the itinerary footer carried the parameters while a later day-band
 * credit did not, and nothing made the difference visible.
 */
const UTM = 'utm_source=vizday&utm_medium=referral';

export const UNSPLASH_HOME = `https://unsplash.com/?${UTM}`;

/** A photographer's Unsplash profile, tagged so they are credited for the referral. */
export function photographerUrl(profileLink: string): string {
  if (!profileLink) return UNSPLASH_HOME;
  return profileLink.includes('?')
    ? `${profileLink}&${UTM}`
    : `${profileLink}?${UTM}`;
}

/**
 * The credit for a thumbnail too small to carry a visible line.
 *
 * At this size the credit cannot be legible text, so it rides the accessible
 * name and a tap-through — which the licence accepts. What it must not do is
 * drop the word Unsplash when the photograph is theirs: a credit reading only
 * "Photo: Annie Spratt" names the photographer and hides the source, and the
 * source is the half the guidelines are actually about.
 */
export function thumbCredit(photographer: string, tier?: string): string {
  if (!photographer) return '';
  return tier === 'pool'
    ? `Photo by ${photographer} on Unsplash`
    : `Photo: ${photographer}`;
}

/**
 * Where that credit links. A venue photo goes to Maps untouched; an Unsplash
 * one carries the referral parameters, exactly as the visible credits do.
 * These links were the drift: the visible lines were centralised here while
 * the tap-throughs kept pointing at the raw profile URL.
 */
export function creditHref(creditUrl: string, tier?: string): string {
  return tier === 'pool' ? photographerUrl(creditUrl) : creditUrl;
}
